/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React, { Component } from 'react';
import { withStyles } from '@material-ui/core/styles';
import PropTypes from 'prop-types';
import { uid } from 'react-uid';

// Material UI
import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import ChevronLeft from '@material-ui/icons/ChevronLeft';
import ChevronRight from '@material-ui/icons/ChevronRight';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';

// Helper
import * as CalendarUtility from './helpers/calendarUtility';

const styles = (theme) => ({
    rootMonths: {
        width: '100%',
    },
    headerContainer: {
        backgroundColor: 'white',
        padding: '20px 24px 10px',
        margin: 0,
        textAlign: 'center',
        [theme.breakpoints.down(670)]: {
            padding: '20px 5% 10px',
        },
        [theme.breakpoints.down(520)]: {
            padding: '20px 4% 10px',
        },
        [theme.breakpoints.down(390)]: {
            padding: '20px 12px 10px',
        },
        [theme.breakpoints.down(408)]: {
            padding: '20px 12px 10px',
        },
    },
    monthHeader: {
        fontSize: '1.1em',
        fontWeight: 'bold',
        margin: 0,
        paddingTop: '2px',
        textTransform: 'capitalize',
        color: theme.palette.unSelectedDeliveryDay || '000',
    },
    weekHeader: {
        fontFamily: theme.fontFamily,
        fontWeight: 'normal',
        marginBottom: '10px',
        color: theme.palette.unSelectedDeliveryDay || '000',
    },
    dayOfTheWeek: {
        margin: '3px 0px',
    },
    alignIcon: {
        display: 'inline-block',
        alignItems: 'center',
        padding: '0px',
        '& svg': {
            color: '#7B7B7B',
        },
    },
    cursorPointer: {
        cursor: 'pointer',
    },
    dayContainer: {
        marginBottom: '8px',
        '& > div:first-child': {
            marginLeft: '4px',
            paddingLeft: '0',
        },
        '& > div:last-child': {
            marginRight: '4px',
            paddingRight: '0',
        },
        [theme.breakpoints.down(520)]: {
            marginBottom: '0',
        },
    },
    selectedBtn: {
        backgroundColor: '#7ed0b1',
    },
    deliveryDateContainer: {
        margin: '0',
    },
    deliveryDayContainer: {
        margin: '0',
        padding: '0 4px',
    },
    deliveryDisableDay: {
        margin: '0',
        padding: '0 4px',
    },
});

const Calendar = ({
    classes,
    calendarData,
    getIconPlaceholder,
    getMediumDayFormat,
    year,
    showNavBtnFrYear,
    showNavBtnFrMonth,
    onClickCalPrev,
    onClickCalNext,
    onSelectedDate,
    overWriteDateComponent,
    selectedDates,
}) => {
    const showDayOfTheWeekHeader = () => CalendarUtility.DaysOfTheWeekShort.map((day, indexDay) => (
        <Grid item xs align="center" key={uid(day, indexDay)}>
            <Typography className={classes.weekHeader}>
                {(getMediumDayFormat) ? day : day[0]}
            </Typography>
        </Grid>
    ));

    const showDay = (day, index) => {
        if (overWriteDateComponent) {
            return overWriteDateComponent(day, selectedDates, onSelectedDate, index, {
                root: classes.deliveryDayContainer,
                disable: classes.deliveryDisableDay,
            });
        }

        if (day) {
            const markAsSelected = (selectedDates.indexOf(day.date) >= 0) ? styles.selectedBtn : {};
            return (
                <Typography key={uid(day, index)} style={styles.dayOfTheWeek}>
                    <Button style={markAsSelected} onClick={() => { onSelectedDate(day.dateObj); }}>
                        {day.dateObj.getDate()}
                    </Button>
                </Typography>
            );
        }

        return (
            <Typography key={uid(day, index)} style={styles.dayOfTheWeek}>
                <Button disabled />
            </Typography>
        );
    };

    const showDaysOfTheWeek = (daysData) => CalendarUtility.DaysOfTheWeekShort.map((day, index) => showDay(daysData[index], index));

    const  showWeeksOfTheMonth = (weeksData) => {
        const Weeks = Object.keys(weeksData);
        return Weeks.map((week, index) => {
            const keyWeek = `week${index}`;
            return (
                <Grid key={keyWeek} container direction="row" spacing={0} className={classes.dayContainer}>
                    {showDaysOfTheWeek(weeksData[week])}
                </Grid>
            );
        });
    };

    const showPrev = (month) => {
        const currentDate = new Date();

        if (showNavBtnFrYear === year && showNavBtnFrMonth === month && (currentDate.getFullYear() !== parseInt(year, 10) || (parseInt(month, 10) !== currentDate.getMonth() + 1))) {
            return (
                <IconButton
                    aria-label="Previous Month"
                    onClick={onClickCalPrev}
                    className={classes.alignIcon}
                    data-test="checkout-delivery-calendar-next-previous-button"
                >
                    <ChevronLeft />
                </IconButton>
            );
        }
        return null;
    };

    const showNext = (month) => {
        if (showNavBtnFrYear === year && showNavBtnFrMonth === month) {
            return (
                <IconButton
                    aria-label="Next Month"
                    onClick={onClickCalNext}
                    data-test="checkout-delivery-calendar-next-month-button"    // do NOT change or remove data-test attribute
                    className={classes.alignIcon}
                >
                    <ChevronRight />
                </IconButton>
            );
        }

        return null;
    };

    const showMonths = (MonthsData) => {
        const Months = Object.keys(MonthsData).sort();

        return Months.map((month, indexMonth) => {
            const keyMonth = `month${indexMonth}`;
            const nameOfTheMonth = CalendarUtility.MonthsFull[parseInt(month, 10) - 1];
            return (
                <Grid id="calendar" key={keyMonth} container className={classes.container}>
                    <Grid item xs={12}>
                        <Grid container className={classes.headerContainer}>
                            <Grid item xs={2} align="left" className={classes.cursorPointer}>
                                {showPrev(month)}
                            </Grid>
                            <Grid item xs={8}>
                                <p className={classes.monthHeader}>
                                    {`${nameOfTheMonth} ${year}`}
                                </p>
                            </Grid>
                            <Grid item xs={2} align="right" className={classes.cursorPointer}>
                                {showNext(month)}
                            </Grid>
                        </Grid>
                    </Grid>
                    <Grid item xs={12} className={classes.deliveryDateContainer}>
                        <Grid container>
                            <Grid item xs={12}>
                                <Grid container>
                                    {showDayOfTheWeekHeader()}
                                </Grid>
                            </Grid>
                            <Grid item xs={12}>
                                <Grid container>
                                    {showWeeksOfTheMonth(MonthsData[month])}
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            );
        });
    };

    return (
        <Grid container className={classes.rootMonths}>
            {showMonths(calendarData[year])}
        </Grid>
    );
};

Calendar.propTypes = {
    calendarData: PropTypes.object.isRequired,
    year: PropTypes.string.isRequired,
    onClickCalNext: PropTypes.func.isRequired,
    onClickCalPrev: PropTypes.func.isRequired,
    showNavBtnFrYear: PropTypes.string.isRequired,
    showNavBtnFrMonth: PropTypes.string.isRequired,
    selectedDates: PropTypes.array.isRequired,
    overWriteDateComponent: PropTypes.func.isRequired,
    onSelectedDate: PropTypes.func.isRequired,
    classes: PropTypes.object.isRequired,
    getMediumDayFormat: PropTypes.bool,
    getIconPlaceholder: PropTypes.bool,
};

Calendar.defaultProps = {
    getMediumDayFormat: false,
    getIconPlaceholder: false,
};

export default withStyles(styles)(Calendar);
